import { ampli } from '@/ampli';
import { useContext, useState } from 'react';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { ExpandIcon } from '@/ui/Icons/ExpandIcon';

import { isNullAmount } from '@/utils/numbers';

import { errorSelector, TradeStore, useTradeStore } from '../../../store/trade';
import { TokenResult } from '../../../utils/0x/token-registry.types';
import { SankeyDiagram } from '../../SankeyDiagram';
import { SankeyLegend } from '../../SankeyDiagram/SankeyLegend';
import { SankeyContext } from '../../SankeyDiagram/SankeyProvider';
import { SankeyPreviewSkeleton } from './SankeyPreviewSkeleton';

type Props = {
  sellToken?: TokenResult;
  buyToken?: TokenResult;
  chainId: number;
};

const sankeyPreviewSelectors = (state: TradeStore) => ({
  currentPrice: state.currentPrice,
  sellInput: state.sellInput,
  checkoutStep: state.checkoutStep,
  quote: state.quote,
});

export function SankeyPreview({ buyToken, chainId }: Props) {
  const { setSankeyModalOpen } = useContext(SankeyContext);
  const { currentPrice, sellInput, quote, checkoutStep } = useTradeStore(sankeyPreviewSelectors);
  const error = useTradeStore(errorSelector);

  const [sankeyContainerRef, setSankeyContainerRef] = useState<HTMLDivElement | null>(null);

  const isPriceOrQuoteLoading =
    !isNullAmount(Number(sellInput.decimal)) &&
    buyToken &&
    checkoutStep === 'select' &&
    !currentPrice;

  const route =
    checkoutStep === 'select'
      ? currentPrice && 'route' in currentPrice
        ? currentPrice.route
        : undefined
      : quote?.route;

  if (error != null || checkoutStep !== 'select' || (route == null && !isPriceOrQuoteLoading)) {
    return null;
  }

  return (
    <div className="bg-special-background-modal shadow-30 pt-3 pb-[18px] px-6 rounded-xl mt-3 mb-3">
      <div className="w-full flex justify-between items-center h-9">
        <span className={fontStyles({ variant: 'LABEL_LG' })}>Route</span>
        {route && (
          <button
            className={buttons({ size: 'small', color: 'transparent', shape: 'rounded' })}
            onClick={() => {
              setSankeyModalOpen(true);
              ampli.sankeyChartExpanded({
                ui_location: 'route_preview',
              });
            }}
          >
            <ExpandIcon aria-hidden className="mr-2" />
            Open
          </button>
        )}
      </div>
      <div className="mt-3 w-full" ref={setSankeyContainerRef}>
        {sankeyContainerRef ? (
          <>
            {isPriceOrQuoteLoading && <SankeyPreviewSkeleton />}
            {route && (
              <SankeyDiagram
                route={route}
                chainId={chainId}
                width={sankeyContainerRef.clientWidth ?? 200}
                height={244}
                isPreview
                isMobile={false}
                onSubRouteTouch={() => {}}
              />
            )}
          </>
        ) : null}
      </div>
      {sankeyContainerRef && route && (
        <div className="mt-6">
          <SankeyLegend route={route} />
        </div>
      )}
    </div>
  );
}
